import React from 'react'
import { Input } from '../../../components/ui/Input'
import { Button } from '../../../components/ui/Button'
import { apiFetch } from '../../../lib/api-client'

interface BankFields {
  bank_reg_nr: string | null
  bank_account_nr: string | null
  iban: string | null
  swift_bic: string | null
}

interface BankTabProps {
  companyId: string
  initial: BankFields
  onSaved: (updated: BankFields) => void
}

const wrap: React.CSSProperties = { marginBottom: 14 }

export function BankTab({ companyId, initial, onSaved }: BankTabProps) {
  const [form, setForm] = React.useState({
    bank_reg_nr: initial.bank_reg_nr ?? '',
    bank_account_nr: initial.bank_account_nr ?? '',
    iban: initial.iban ?? '',
    swift_bic: initial.swift_bic ?? '',
  })
  const [saving, setSaving] = React.useState(false)
  const [saved, setSaved] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      const updated = await apiFetch<BankFields>(`/api/v1/companies/${companyId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          bank_reg_nr: form.bank_reg_nr.trim() || null,
          bank_account_nr: form.bank_account_nr.trim() || null,
          iban: form.iban.trim() || null,
          swift_bic: form.swift_bic.trim() || null,
        }),
      })
      onSaved(updated)
      setSaved(true)
    } catch {
      setError('Kunne ikke gemme. Prøv igen.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ maxWidth: 520, padding: '24px 28px' }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>Bankinformation</div>
        <div style={{ fontSize: 12, color: '#64748b' }}>
          Lejerens indbetalingskonto for husleje og depositum. Vises på rykkerskrivelser og indbetalingskort.
        </div>
      </div>

      <form onSubmit={handleSave}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginBottom: 14 }}>
          <Input
            label="Reg.nr."
            value={form.bank_reg_nr}
            onChange={(e) => setForm({ ...form, bank_reg_nr: e.target.value })}
            placeholder="1234 (4 cifre)"
            maxLength={4}
          />
          <Input
            label="Kontonr."
            value={form.bank_account_nr}
            onChange={(e) => setForm({ ...form, bank_account_nr: e.target.value })}
            placeholder="1234567890 (10 cifre)"
            maxLength={10}
          />
        </div>
        <div style={wrap}>
          <Input
            label="IBAN"
            value={form.iban}
            onChange={(e) => setForm({ ...form, iban: e.target.value })}
            placeholder="DK12 3456 7890 1234 56"
            maxLength={34}
          />
        </div>
        <div style={wrap}>
          <Input
            label="SWIFT/BIC"
            value={form.swift_bic}
            onChange={(e) => setForm({ ...form, swift_bic: e.target.value })}
            placeholder="DABADKKK"
            maxLength={11}
          />
        </div>

        {error && <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 12 }}>{error}</p>}
        {saved && <p style={{ fontSize: 12, color: '#16a34a', marginBottom: 12 }}>Gemt.</p>}

        <Button type="submit" isLoading={saving}>Gem</Button>
      </form>
    </div>
  )
}
